import React from "react";
import { useIntl } from "react-intl";
import { messages } from "../Mixing.messages";
import styles from "./Slides.module.scss";
import { SimpleLayoutView } from "@src/lms/jelly/extensions/layouts/simple/SimpleLayoutView";
import { nl2p } from "@src/utils/nl2p";
import { useUi } from "@jelly/lesson";

import {
  PanningSingleModal,
  PanningStereoModal,
  MultiInstancesModal,
  UpmixingModal,
  QuadBusModal,
  ThreeDReverbModal,
  LfeModal,
} from "../Modals/Modals";

interface SlideProps {}

export const Slide1: React.FC<SlideProps> = () => {
  const { formatMessage } = useIntl();
  const { Button } = useUi();

  // Modal state management
  const [panningSingleModalOpen, setPanningSingleModalOpen] = React.useState(false);
  const [panningStereoModalOpen, setPanningStereoModalOpen] = React.useState(false);

  const handleClosePanningSingleModal = React.useCallback(() => setPanningSingleModalOpen(false), []);
  const handleClosePanningStereoModal = React.useCallback(() => setPanningStereoModalOpen(false), []);

  return (
    <SimpleLayoutView>
      <div className={styles.root}>
        <div className={styles.rightComponent}>
          <div className={styles.oneColumn}>
            <h3>{formatMessage(messages.G2_P4_S1_Title)}</h3>
            {nl2p(formatMessage(messages.G2_P4_S1_Description))}

            <div className={styles.titleWithButton}>
              <h4>{formatMessage(messages.G2_P4_S1_P1_Title)}</h4>
            </div>
            {nl2p(formatMessage(messages.G2_P4_S1_P1_Description))}

            <div className={styles.titleWithButton}>
              <h4>{formatMessage(messages.G2_P4_S1_P2_Title)}</h4>
              <Button
                size={"small"}
                theme={"primary"}
                shape={"round"}
                icon={"cil-chevron-top-alt"}
                onClick={() => setPanningSingleModalOpen(true)}
              >
                {formatMessage(messages.G2_P4_S1_P1_ButtonLabel1)}
              </Button>
              <Button
                size={"small"}
                theme={"primary"}
                shape={"round"}
                icon={"cil-library-add"}
                onClick={() => setPanningStereoModalOpen(true)}
              >
                {formatMessage(messages.G2_P4_S1_P1_ButtonLabel2)}
              </Button>
            </div>
            {nl2p(formatMessage(messages.G2_P4_S1_P2_Description))}
          </div>
        </div>

        {/* Modals */}
        <PanningSingleModal visible={panningSingleModalOpen} onClose={handleClosePanningSingleModal} />
        <PanningStereoModal visible={panningStereoModalOpen} onClose={handleClosePanningStereoModal} />
      </div>
    </SimpleLayoutView>
  );
};

export const Slide2: React.FC<SlideProps> = () => {
  const { formatMessage } = useIntl();
  const { Button } = useUi();

  // Modal state management
  const [upmixingModalOpen, setUpmixingModalOpen] = React.useState(false);

  const handleCloseUpmixingModal = React.useCallback(() => setUpmixingModalOpen(false), []);

  return (
    <SimpleLayoutView>
      <div className={styles.root}>
        <div className={styles.rightComponent}>
          <div className={styles.oneColumn}>
            <h3>{formatMessage(messages.G2_P4_S2_Title)}</h3>
            {nl2p(formatMessage(messages.G2_P4_S2_Description))}

            <div className={styles.titleWithButton}>
              <h4>{formatMessage(messages.G2_P4_S2_P1_Title)}</h4>
              <Button
                size={"small"}
                theme={"primary"}
                shape={"round"}
                icon={"cil-settings"}
                onClick={() => setUpmixingModalOpen(true)}
              >
                {formatMessage(messages.G2_P4_S2_P1_ButtonLabel)}
              </Button>
            </div>
            {nl2p(formatMessage(messages.G2_P4_S2_P1_Description))}
          </div>
        </div>

        {/* Modals */}
        <UpmixingModal visible={upmixingModalOpen} onClose={handleCloseUpmixingModal} />
      </div>
    </SimpleLayoutView>
  );
};

export const Slide3: React.FC<SlideProps> = () => {
  const { formatMessage } = useIntl();
  const { Button } = useUi();

  // Modal state management
  const [multiInstancesModalOpen, setMultiInstancesModalOpen] = React.useState(false);

  const handleCloseMultiInstancesModal = React.useCallback(() => setMultiInstancesModalOpen(false), []);

  return (
    <SimpleLayoutView>
      <div className={styles.root}>
        <div className={styles.rightComponent}>
          <div className={styles.oneColumn}>
            <h3>{formatMessage(messages.G2_P4_S3_Title)}</h3>
            {nl2p(formatMessage(messages.G2_P4_S3_Description))}

            <div className={styles.centeredButton}>
              <Button
                size={"small"}
                theme={"primary"}
                shape={"round"}
                icon={"cil-library-add"}
                onClick={() => setMultiInstancesModalOpen(true)}
              >
                {formatMessage(messages.G2_P4_S3_ButtonLabel)}
              </Button>
            </div>
          </div>
        </div>

        {/* Modals */}
        <MultiInstancesModal visible={multiInstancesModalOpen} onClose={handleCloseMultiInstancesModal} />
      </div>
    </SimpleLayoutView>
  );
};

export const Slide4: React.FC<SlideProps> = () => {
  const { formatMessage } = useIntl();
  const { Button } = useUi();

  // Modal state management
  const [quadBusModalOpen, setQuadBusModalOpen] = React.useState(false);
  const handleCloseQuadBusModal = React.useCallback(() => setQuadBusModalOpen(false), []);

  return (
    <SimpleLayoutView>
      <div className={styles.root}>
        <div className={styles.rightComponent}>
          <div className={styles.oneColumn}>
            <h3>{formatMessage(messages.G2_P4_S4_Title)}</h3>
            {nl2p(formatMessage(messages.G2_P4_S4_Description))}
            <div className={styles.centeredButton}>
              <Button
                size={"small"}
                theme={"primary"}
                shape={"round"}
                onClick={() => setQuadBusModalOpen(true)}
                icon={"cil-border-all"}
              >
                {formatMessage(messages.G2_P4_S4_ButtonLabel)}
              </Button>
            </div>
          </div>
        </div>

        {/* Modals */}
        <QuadBusModal visible={quadBusModalOpen} onClose={handleCloseQuadBusModal} />
      </div>
    </SimpleLayoutView>
  );
};

export const Slide5: React.FC<SlideProps> = () => {
  const { formatMessage } = useIntl();
  const { Button } = useUi();

  // Modal state management
  const [threeDReverbModalOpen, setThreeDReverbModalOpen] = React.useState(false);

  const handleCloseThreeDReverbModal = React.useCallback(() => setThreeDReverbModalOpen(false), []);

  return (
    <SimpleLayoutView>
      <div className={styles.root}>
        <div className={styles.rightComponent}>
          <div className={styles.oneColumn}>
            <h3>{formatMessage(messages.G2_P4_S5_Title)}</h3>
            {nl2p(formatMessage(messages.G2_P4_S5_Description))}

            <div className={styles.centeredButton}>
              <Button
                size={"small"}
                theme={"primary"}
                shape={"round"}
                onClick={() => setThreeDReverbModalOpen(true)}
                icon={"cil-3d"}
              >
                {formatMessage(messages.G2_P4_S5_ButtonLabel)}
              </Button>
            </div>
          </div>
        </div>

        {/* Modals */}
        <ThreeDReverbModal visible={threeDReverbModalOpen} onClose={handleCloseThreeDReverbModal} />
      </div>
    </SimpleLayoutView>
  );
};

export const Slide6: React.FC<SlideProps> = () => {
  const { formatMessage } = useIntl();
  const { Button } = useUi();

  // Modal state management
  const [lfeModalOpen, setLfeModalOpen] = React.useState(false);

  const handleCloseLfeModal = React.useCallback(() => setLfeModalOpen(false), []);

  return (
    <SimpleLayoutView>
      <div className={styles.root}>
        <div className={styles.rightComponent}>
          <div className={styles.oneColumn}>
            <h3>{formatMessage(messages.G2_P4_S6_Title)}</h3>
            {nl2p(formatMessage(messages.G2_P4_S6_Description))}

            <div className={styles.centeredButton}>
              <Button
                size={"small"}
                theme={"primary"}
                shape={"round"}
                onClick={() => setLfeModalOpen(true)}
                icon={"cil-equalizer"}
              >
                {formatMessage(messages.G2_P4_S6_ButtonLabel)}
              </Button>
            </div>
          </div>
        </div>

        {/* Modals */}
        <LfeModal visible={lfeModalOpen} onClose={handleCloseLfeModal} />
      </div>
    </SimpleLayoutView>
  );
};
